import './rich-text-editor.scss';
import { EditorContent, useEditor } from '@tiptap/react';
import { deserialize, serialize } from './rich-text-editor.utils';
import Link from '@tiptap/extension-link';
import { RichTextBoxMenuBar } from './rich-text-editor.menu-bar';
import StarterKit from '@tiptap/starter-kit';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import { useEffect, useRef, useState } from 'react';
interface IRichTextEditor {
  value: string;
  onChange: (newValue: string) => void;
  editable: boolean;
  hideMenu?: boolean;
  showToolbarEvenIfNotEditable?: boolean;
  onBlur?: (newValue: string) => void;
  minHeightForTextFile?: number;
  dataTestId?: string;
}

function deepEqual<T>(a: T, b: T): boolean {
  if (a === b) return true;

  if (
    typeof a !== 'object' ||
    typeof b !== 'object' ||
    a === null ||
    b === null
  )
    return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  const keysA = Object.keys(a) as (keyof T)[];
  const keysB = Object.keys(b) as (keyof T)[];
  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false;
  }

  return true;
}

export const RichTextEditor = ({
  value,
  onChange,
  hideMenu,
  editable = true,
  onBlur,
  minHeightForTextFile,
  showToolbarEvenIfNotEditable,
  dataTestId,
}: IRichTextEditor) => {
  const [defaultValue, setDefaultValue] = useState(false);
  const lastSentRef = useRef(value || '');
  const [lastSentValue, setLastSentValue] = useState<string>(value || '');
  const editor = useEditor({
    extensions: [StarterKit.configure(), TaskList, TaskItem, Link],
    onCreate({ editor }) {
      editor.commands.setContent(deserialize(editor.schema, value || ''));
    },
    onUpdate: ({ editor }) => {
      if (!editable) {
        return;
      }
      const newValue = serialize(editor.schema, editor.getJSON());
      const equalss = deepEqual(
        editor.getJSON(),
        deserialize(editor.schema, lastSentRef.current || '')
      );
      if (!equalss) {
        onChange(newValue);
        setLastSentValue(newValue);
      }
    },
    onBlur: ({ editor }) => {
      if (!editable) {
        return;
      }

      if (onBlur) {
        const newValue = serialize(editor.schema, editor.getJSON());

        if (newValue !== value) {
          onBlur(newValue);
        }
      }
    },
    editable,
  });

  useEffect(() => {
    lastSentRef.current = lastSentValue;
  }, [lastSentValue]);

  useEffect(() => {
    if (editor && value && !defaultValue) {
      setDefaultValue(true);
      editor.commands.setContent(deserialize(editor.schema, value || ''));
    }
  }, [editor, defaultValue, value]);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editable, editor]);

  const showMenu = (editable || showToolbarEvenIfNotEditable) && !hideMenu;

  return (
    <div className="editor">
      {editor && showMenu && (
        <RichTextBoxMenuBar disabled={!editable} editor={editor} />
      )}
      {editor && (
        <EditorContent
          className="editor-content"
          editor={editor}
          // value={value || ''}
          contentEditable={!editable}
          style={{ minHeight: minHeightForTextFile }}
          data-testid={dataTestId}
        />
      )}
    </div>
  );
};
